import React, { forwardRef, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Icon from '../icon/icon';
import styles from './search-input.module.scss';

type SearchInputProps = {
  placeholder: string;
  value: string | string[];
  onChange: (arg: string) => void;
  onReset?: () => void;
  hideIcon?: boolean;
  identifier?: string;
  filterName?: string;
} & React.HTMLProps<HTMLInputElement>;

const SearchInput = forwardRef<{ reset: () => void }, SearchInputProps>((props, ref) => {
  const { t: tA11y } = useTranslation('a11y');
  const { placeholder, value, hideIcon = false, onChange, onReset, identifier, filterName } = props;

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const handleReset = () => {
    onReset();
  };

  const handleEscPress = (e) => {
    if (e.code === 'Escape') {
      handleReset();
    }
  };

  useImperativeHandle(ref, () => ({ reset: handleReset }), []);

  return (
    <div className={styles.searchInputWrapper}>
      {!hideIcon && (
        <Icon
          name="search-default" // DS icon
          className={styles.searchIcon}
          ariaHidden
        />
      )}
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        className={clsx(styles.searchInput, { [styles.withIcon]: !hideIcon })}
        onKeyDown={handleEscPress}
        data-cy={identifier}
        name={filterName}
        aria-label={filterName}
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={handleReset}
          tabIndex={0}
          className={styles.clearIconWrapper}
          aria-label={tA11y('A11Y.ICON.CLEAR')}
        >
          <Icon
            name="close-default"
            className={styles.clearIcon}
            ariaHidden
          />
        </button>
      )}
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
